import { useMemo } from "react";
import { usePlanningTabularContext } from "@/providers/PlanningTabularProvider";
import { GetDoc } from "@/Utils/StateLib";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { ColumnDef, CellContext } from "@tanstack/react-table";
import {
  createColumnWithMeta,
  getIsAllPageRowsSelected,
  getSelectionColumn,
  renderCellValue,
  toggleAllPageRowsSelected
} from "@theorgchartdev/storybook";
import { enumFieldType, posObjClassFieldIds } from "@coreapp/constants";
import { boxObjClassFieldIds } from "@coreapp/constants";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import {
  createAssignmentsColumn,
  createDesiredHoldersColumn,
  createParentPositionColumn,
  getOverflowText,
  isParentOrSubRow
} from "@/Utils/Table/PosMgmtUtils";
import {
  FormatFieldValue,
  getBlockedFieldDisplayValue,
  isBlockedField
} from "@coreapp/utils/FormatFieldValue";

export const useTableColumns = (): ColumnDef<any>[] => {
  const {
    state,
    fetchColumnFilterOptions,
    toastHighlightSelectionRef,
    toastHighlightExpansionRef
  } = usePlanningTabularContext();
  const { planManager } = usePlanManagerContext();
  const isPlanEditable = planManager?.state?.CanUpdatePlan();

  return useMemo(() => {
    const cols: ColumnDef<any>[] = [];
    const doc = GetDoc();
    const objDb = doc?.GetObjDB();

    if (!objDb) {
      return cols;
    }

    const selectionColumn = getSelectionColumn({
      headerProps: {
        getIsChecked: table => getIsAllPageRowsSelected(table),
        getIsIndeterminate: table => {
          const isAllChecked = getIsAllPageRowsSelected(table);
          const isSomeChecked = table.getIsSomePageRowsSelected();
          return isSomeChecked && !isAllChecked;
        },
        onHeaderCheckboxChange: (table, isCurrentlyChecked) => {
          toggleAllPageRowsSelected(table, !isCurrentlyChecked);
        }
      }
    }) as ColumnDef<any>;

    if (isPlanEditable) {
      cols.push(selectionColumn);
    }

    for (const fieldDef of state.columns as QObjDBFieldDef[]) {
      if (!fieldDef) {
        continue;
      }

      const fieldID = fieldDef.GetFieldID();
      const fieldName = fieldDef.GetFieldName();
      const fieldObjClassName = fieldDef.GetFieldClassName();
      const fieldType = fieldDef.GetFieldType();

      const commonDef: ColumnDef<any> = createColumnWithMeta<any, any>(
        {
          id: `${fieldID}_${fieldObjClassName}`,
          accessorFn: (row: any) => {
            if (row.dataType === fieldObjClassName) {
              return row[fieldName];
            }
            if (row.subRows?.length === 1) {
              return row.subRows[0][fieldName] ?? "";
            }
            if (row.subRows?.length > 1) {
              return row.subRows.map((subRow: any) => subRow[fieldName] ?? "").join(", ");
            }
            return "";
          },
          header: () => getOverflowText(fieldDef.GetFieldLabel()),
          size: 175,
          cell: ({ row, getValue, column }: CellContext<any, string>) => {
            if (isParentOrSubRow(row) && row.original.dataType !== fieldObjClassName) {
              return "";
            }

            const value = getValue();
            let formattedValue: string;
            // Backend is responsible for date conversions and formatting. So avoid it here.
            if (fieldType === enumFieldType.date) {
              if (isBlockedField(value)) {
                formattedValue = getBlockedFieldDisplayValue();
              } else {
                formattedValue = value;
              }
            } else {
              formattedValue = FormatFieldValue(value, fieldDef);
            }
            return renderCellValue({
              row,
              value: formattedValue,
              column,
              renderValue: () => getOverflowText(formattedValue)
            });
          }
        },
        {
          dataType: fieldObjClassName ?? undefined,
          filterOptions: {
            fetchOptions: fetchColumnFilterOptions
          }
        }
      );

      switch (fieldID) {
        case posObjClassFieldIds.POSITION_ID:
          cols.push({ ...commonDef, size: 150 });
          break;

        case posObjClassFieldIds.DESIRED_HOLDERS:
          cols.push(createDesiredHoldersColumn(commonDef, objDb));
          break;

        case posObjClassFieldIds.PARENT_POSITION_ID:
          cols.push(createParentPositionColumn(commonDef, objDb));
          break;

        case posObjClassFieldIds.POSITION_LOCATION:
          cols.push({
            ...commonDef,
            size: 200
          });
          break;

        case posObjClassFieldIds.POSITION_TITLE:
          cols.push({
            ...commonDef,
            size: 300
          });
          break;

        case boxObjClassFieldIds.NAME:
          cols.push(
            createAssignmentsColumn(
              commonDef,
              fieldName,
              toastHighlightSelectionRef,
              toastHighlightExpansionRef
            )
          );
          break;

        default:
          cols.push(commonDef);
      }
    }
    return cols;
  }, [state.columns, fetchColumnFilterOptions, isPlanEditable]);
};
